import { Modal, ModalOverlay, ModalContent, Flex, Image, Text, Box, useToast } from '@chakra-ui/react'
import {
  Button as UbiButton,
  Column,
  Row,
  FormItem,
  TextInput,
  NumberInput,
  Radio,
} from '@ubirider/pick-component-library'
import { useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import arrowBackIcon from '../../assets/arrow_left.svg'
import checkWhiteIcon from '../../assets/check_circle_white.svg'
import closeWhiteIcon from '../../assets/cross_white_24.svg'
import cardIcon from '../../assets/credit_card.svg'
import calendarIcon from '../../assets/calendar.svg'
import { DateTime } from 'luxon'
import { addMoneyToPickCard } from '../../api/cards'
import visa from '../../assets/visa.svg'
import mastercard from '../../assets/mastercard.svg'
import amex from '../../assets/amex.svg'
import unionpay from '../../assets/unionpay.svg'
import discover from '../../assets/discover.svg'

export function AddMoneyModal(props) {
  const { t } = useTranslation()
  const windowSize = useRef([window.innerWidth, window.innerHeight])

  const {
    control,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: null,
      payment_method: null,
      card_number: null,
      expiration: null,
      cvv: null,
    },
  })

  const quickAmounts = [10, 50, 100]
  const [loading, setLoading] = useState(false)
  const [formStage, setFormStage] = useState(1)

  const detectCardBrand = firstSixDigits => {
    switch (true) {
      case /^4/.test(firstSixDigits):
        return visa
      case /^5[1-5]/.test(firstSixDigits):
        return mastercard
      case /^(34|37)/.test(firstSixDigits):
        return amex
      case /^62/.test(firstSixDigits):
        return unionpay
      case /^6/.test(firstSixDigits):
        return discover
      default:
        return cardIcon
    }
  }

  const amount = watch('amount')
  const paymentMethod = watch('payment_method')
  console.log(paymentMethod)
  const cardNumber = watch('card_number')
  const expirationDate = watch('expiration')
  const CVV = watch('cvv')
  const cardBrand = detectCardBrand(cardNumber?.replace(/\s/g, '').slice(0, 6) || '')

  const isDisabled = () => {
    if (formStage === 1) {
      return !amount || !paymentMethod
    } else if (formStage === 2) {
      return !cardNumber || !expirationDate || !CVV
    }
  }

  useEffect(() => {
    reset({
      amount: null,
      payment_method: null,
      card_number: null,
      expiration: null,
      cvv: null,
    })
    setLoading(null)
  }, [props.isOpen, props.onClose, reset])

  const onSubmit = async values => {
    await setLoading(true)
    try {
      console.log(values)
      const data = {
        amount: values.amount,
        bank: paymentMethod === 1,
        cardNumber: paymentMethod === 2 ? `${values.card_number.replace(/\s/g, '').slice(0, 6)}******${values.card_number.replace(/\s/g, '').slice(-4)}` : null,
        date: DateTime.utc().toMillis(),
      }

      addMoneyToPickCard(props.pickCard.token, data).then(() => {
        setLoading(false)
        handleToast()
        props.onClose()
      })
    } catch (error) {
      setLoading(false)
      console.log(error.message)
    }
  }

  const handleToast = () => {
    toastIdRef.current = toast({
      position: 'top',
      duration: 5000,
      isClosable: true,
      render: () => (
        <Box alignItems='center' color='white' bg='#01AE70' border='1px solid #067F5B' boxShadow='box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.05)' borderRadius='4px'>
          <Flex justifyContent='space-between' gap='16px' padding='16px'>
            <Image src={checkWhiteIcon} />
            <Text fontSize='14px' fontWeight='600'>
              {t('demo.money_added_toast')}
            </Text>
            <Image cursor='pointer' onClick={close} src={closeWhiteIcon} />
          </Flex>
        </Box>
      ),
    })
  }

  const toast = useToast()
  const toastIdRef = useRef()

  function close() {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current)
    }
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      onOpen={props.onOpen}
      trapFocus={false}
      closeOnOverlayClick={false}
      autoFocus={false}
      size={{ sm: 'full', md: 'full', lg: 'xl', xl: 'xl' }}
      isCentered={windowSize.current[0] >= 960 || props?.blueInfo}
    >
      <ModalOverlay />
      <ModalContent
        margin={{
          sm: '',
          md: '0',
          lg: '0',
          xl: '0',
        }}
        padding={{ sm: '16px 24px', md: '16px 24px', lg: '32px', xl: '32px' }}
      >
        <Flex direction='column' gap='48px' mt={{ sm: '67px', md: '67px', lg: '0px', xl: '0px' }}>
          <Flex direction='column' gap='16px'>
            <Row css={{ gap: 24, alignItems: 'center' }}>
              {formStage > 1 && (
                <UbiButton
                  onClick={() => setFormStage(formStage - 1)}
                  style='typo'
                  type='neutralDark'
                  leftIconProps={{ src: arrowBackIcon, fill: '$neutral800' }}
                />
              )}
              {formStage === 1 && (
                <Text fontWeight='700' fontSize='24px'>
                  {t('demo.add_money')}
                </Text>
              )}
              {formStage === 2 && (
                <Text fontWeight='700' fontSize='24px'>
                  {t('demo.card_details')}
                </Text>
              )}
              {formStage === 3 && (
                <Text fontWeight='700' fontSize='24px'>
                  {t('demo.confirm_top_up')}
                </Text>
              )}
            </Row>
            {formStage === 1 && (
              <Text fontSize='16px'>
                {t('demo.add_money_description')}
              </Text>
            )}
            {formStage === 3 && (
              <Text color='neutral.600' fontSize='16px'>
                {t('demo.confirm_top_up_description')}
              </Text>
            )}
          </Flex>

          <Column as='form' onSubmit={handleSubmit(onSubmit)} css={{ gap: '48px' }}>
            {formStage === 1 &&
              <Column css={{ gap: '48px' }}>
                <Controller
                  name='amount'
                  control={control}
                  render={({ field }) => (
                    <FormItem error={errors.amount}>
                      <Column css={{ gap: 24, flex: 1 }}>
                        <Row css={{ gap: 16 }}>
                          {quickAmounts.map(a => (
                            <UbiButton
                              key={a}
                              label={`${a}€`}
                              size='large'
                              style='outline'
                              active={amount === a}
                              css={{ flex: 1 }}
                              onClick={() => setValue('amount', a, { shouldDirty: true, shouldValidate: true })}
                            />
                          ))}
                        </Row>
                        <Column css={{ gap: 8 }}>
                          <Text fontSize='14px' fontWeight='600' color='#667085'>{t('demo.enter_custom_amount')}</Text>
                          <NumberInput unit='€' unitPlacement='before' float='true' defaultValue={0} background='white' {...field} />
                        </Column>
                      </Column>
                    </FormItem>
                  )}
                />

                  <Controller
                    name='payment_method'
                    control={control}
                    render={({ field }) => (
                      <FormItem label={t('demo.choose_payment_method')} error={errors.payment_method}>
                        <Column css={{ padding: 8, borderRadius: 16, border: '1px solid $neutral200', width: '100%' }}>
                          <Row css={{ padding: 16, gap: 16, borderBottom: '1px solid $neutral200' }}>
                            <Radio checked={field.value === 1} onChange={() => field.onChange(1)} />
                            <Text
                              fontSize='16px'
                              fontWeight='700'
                              cursor='pointer'
                              onClick={() => field.onChange(1)}
                            >
                              {t('demo.bank_account')}
                            </Text>
                          </Row>
                          <Row css={{ padding: 16, gap: 16 }}>
                            <Radio checked={field.value === 2} onChange={() => field.onChange(2)} />
                            <Text
                              fontSize='16px'
                              fontWeight='700'
                              cursor='pointer'
                              onClick={() => field.onChange(2)}
                            >
                              {t('demo.credit_or_debit_card')}
                            </Text>
                          </Row>
                        </Column>
                      </FormItem>
                    )}
                  />
              </Column>
            }

            {formStage === 2 &&
              <Column css={{ gap: '24px' }}>
                <Controller
                  name='card_number'
                  control={control}
                  rules={{
                    required: t('authentication.required_field'),
                  }}
                  render={({ field }) => (
                    <FormItem required label={t('demo.card_details')} error={errors.card_number}>
                      <TextInput
                        {...field}
                        placeholder={'1234 **** **** 7890'}
                        leftIconSrc={cardBrand}
                        value={field.value || ''}
                        onChange={e => {
                          const raw = typeof e === 'string' ? e : e?.target?.value ?? ''
                          let digits = raw.replace(/\D/g, '').slice(0, 16)
                          const formatted = digits.replace(/(.{4})/g, '$1 ').trim()
                          field.onChange(formatted)
                        }}
                        onKeyDown={e => {
                          const ok = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
                          if (e.ctrlKey || e.metaKey || ok.includes(e.key)) return
                          if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault()
                        }}
                      />
                    </FormItem>
                  )}
                />

                <Row css={{ gap: 8 }}>
                  <Controller
                    name='expiration'
                    control={control}
                    rules={{
                      required: t('authentication.required_field'),
                    }}
                    render={({ field }) => (
                      <FormItem required label={t('demo.expiration_date')} error={errors.expiration}>
                        <TextInput
                          {...field}
                          placeholder='MM-YY'
                          rightIconSrc={{ src: calendarIcon }}
                          value={field.value || ''}
                          onChange={e => {
                            const raw = e ?? ''
                            const digits = raw.replace(/\D/g, '').slice(0, 4)
                            const formatted =
                              digits.length < 2
                                ? digits
                                : digits.length === 2
                                  ? `${digits}-`
                                  : `${digits.slice(0, 2)}-${digits.slice(2)}`
                            field.onChange(formatted)
                          }}
                          onKeyDown={e => {
                            const el = e.target
                            const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
                            if (e.ctrlKey || e.metaKey) return
                            if (nav.includes(e.key)) {
                              if (e.key === 'Backspace') {
                                const val = el.value ?? ''
                                const pos = el.selectionStart ?? 0
                                if (pos > 0 && val[pos - 1] === '-') {
                                  e.preventDefault()
                                  const before = val.slice(0, pos - 1)
                                  const before2 = before.slice(0, -1)
                                  const after = val.slice(pos)
                                  const digits = (before2 + after).replace(/\D/g, '').slice(0, 4)
                                  const next =
                                    digits.length < 2
                                      ? digits
                                      : digits.length === 2
                                        ? `${digits}-`
                                        : `${digits.slice(0, 2)}-${digits.slice(2)}`
                                  field.onChange(next)
                                  requestAnimationFrame(() => {
                                    const caret = Math.min(before2.length, next.length)
                                    el.setSelectionRange(caret, caret)
                                  })
                                }
                              }
                              return
                            }
                            if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault()
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormItem>
                    )}
                  />

                  <Controller
                    name='cvv'
                    control={control}
                    rules={{
                      required: t('authentication.required_field'),
                    }}
                    render={({ field }) => (
                      <FormItem
                        required
                        label={t('demo.cvv')}
                        tooltip={t('demo.cvv_tooltip')}
                        error={errors.cvv}
                      >
                        <TextInput
                          {...field}
                          placeholder='123'
                          value={field.value ?? ''}
                          inputMode='numeric'
                          pattern='\d*'
                          onChange={e => {
                            const raw = typeof e === 'string' ? e : e?.target?.value ?? ''
                            const digits = raw.replace(/\D/g, '').slice(0, 3) // only 3 digits allowed
                            field.onChange(digits)
                          }}
                          onKeyDown={e => {
                            const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
                            if (e.ctrlKey || e.metaKey || nav.includes(e.key)) return
                            if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault()
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormItem>
                    )}
                  />
                </Row>
              </Column>
            }

            {formStage === 3 &&
              <Column css={{ gap: 8 }}>
                <Text fontSize='48px' fontWeight='700' color='#000'>
                  {amount?.toFixed(2) ?? '0.00'}€
                </Text>
                {paymentMethod === 1 ?
                  <Text fontSize='18px'>
                    {t('demo.bank_account')}
                  </Text> :
                  <Text fontSize='18px'>
                    {t('travel_history.card')} ** {cardNumber.replace(/\s/g, '').slice(-4)}
                  </Text>}
              </Column>
            }

            <Row css={{ justifyContent: 'flex-end', gap: 16 }}>
              <UbiButton
                onClick={props.onClose}
                style='solid'
                type='neutralLight'
                label={t('common.cancel')}
                css={{ width: 'fit-content', minWidth: 128 }}
              />
              {formStage < 3 &&
                <UbiButton
                  onClick={() => setFormStage(paymentMethod === 1 ? formStage + 2 : formStage + 1)}
                  disabled={isDisabled()}
                  loading={loading}
                  style='solid'
                  type='primary'
                  label={t('home.continue')}
                  css={{ width: 'fit-content', minWidth: 128 }}
                />
              }
              {formStage === 3 &&
                <UbiButton
                  submit
                  loading={loading}
                  style='solid'
                  type='primary'
                  label={t('demo.add_money_safely')}
                  css={{ width: 'fit-content', minWidth: 128 }}
                />
              }
            </Row>
          </Column>
        </Flex>
      </ModalContent>
    </Modal>
  )
}
